import React, {useState, memo, useEffect, useContext} from 'react';
import {makeStyles} from '@material-ui/styles';
import theme from "@components/theme";
import MenuItem from "./mobileMenuItem";
import { Button } from "@material-ui/core";
import {useTranslation} from '@tools/i18n';
import {useRouter} from "next/router";
import {TrackingContext} from "@components/providers/tracking";
import Link from "next/link";
import CloseIcon from "@public/images/icons/close.svg";
import useSessionStore from "@services/stores/SessionStore";
import useApplicationStore from '@services/stores/ApplicationStore';
import NewportDrawer from '@components/drawer';
import Account from "../account";
import MobileMenuItem from './mobileMenuItem';

const styles =  {
	navigation__wrapper: {
		display: 'flex',
		width: '100%',
		height: '100%',
		padding: 0,
		background: theme.palette.header.menu.background,
	},
	navigation__inner: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		paddingTop: '10px'
	},
	navigation__list: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		width: '100%',
		padding: 0,
		margin: 0,
		listStyle: 'none',
        paddingBottom: 120,
	},
	navigation__item: {
		display: 'flex',
		flexDirection: 'column',
		minHeight: 0,
		borderBottom: 'none',
		overflow: 'hidden',
		'& > ul': {
			height: '0',
			borderLeft: '2px solid #ddd',
			paddingLeft: '15px',
			'&.level-2': {
				borderLeftColor: '#ddd',
			},
			'& > li': {
				transform: 'translate(-15px, -2px)',
				transformOrigin: 'center',
				transition: '0.15s ease-out',
				willChange: 'transform',
			}
		},

		'&.opened': {
			'& > button': {
				fontWeight: 600,
			},
			'& > ul': {
				height: 'unset',
				'& > li': {
					transform: 'translate(0, 0)',
					'&:nth-child(1)': { transitionDelay: '0ms' },
					'&:nth-child(2)': { transitionDelay: '30ms' },
					'&:nth-child(3)': { transitionDelay: '60ms' },
					'&:nth-child(4)': { transitionDelay: '90ms' },
					'&:nth-child(5)': { transitionDelay: '120ms' },
					'&:nth-child(6)': { transitionDelay: '150ms' },
					'&:nth-child(7)': { transitionDelay: '180ms' },
					'&:nth-child(8)': { transitionDelay: '210ms' },
					'&:nth-child(9)': { transitionDelay: '240ms' },
					'&:nth-child(10)': { transitionDelay: '270ms' },
				}
			},
		},

		'& > button': {
			fontWeight: 600,
			'& svg': {
				color: theme.palette.header.menu.arrows,
				position: 'absolute',
				right: 0,
				transform: 'rotate(-90deg)'
			}
		},

		'& .back': {
			width: 32,
			height: 32,
			padding: 8
		},
		'& button, & a': {
			display: 'flex',
			width: '100%',
			minHeight: 0,
			fontSize: 14,
			fontWeight: 300,
			fontFamily: theme.typography.headerFont,
			color: theme.palette.header.menu.items.level2,
			textDecoration: 'none',
			textAlign: 'center',
		},
		'& button': {
			padding: '12px 0',
		},
		'& .indicator': {
			width: 32,
			height: 32,
			margin: '-5px 0 -5px auto',
			padding: 8,
			borderRadius: 3,
			color: '#aaa',
			transform: 'rotate(-90deg)'
		},
		'&.header': {
			...theme.typography.h2,
			flexDirection: 'row',
			alignItems: 'center',
			padding: '0',
			fontSize: 24,
			fontWeight: 300,
			textTransform: 'uppercase',
			textAlign: 'center',
			borderBottom: 'none',
			'& .close': {
				color: theme.palette.header.menu.items.level2,
				position: 'absolute',
				top: 0,
				right: 0,
				marginLeft: 'auto',
				padding: 8
			}
		},

	},
	toplink: {
		marginBottom: '10px',
		'& a': {
			fontSize: '1.2em',
		},
		
		'&.last-space': {
			borderBottom: '1px solid #eee',
			paddingBottom: '10px',
			marginBottom: '20px',
		},
	},

	bottomlink: {
		fontFamily: theme.typography.headerFont,
		padding: '12px 0',
		'& a': {
			fontSize: '1.2em',
		},
        '&.account': {
            display: 'flex',
            justifyContent: 'space-between',
            gap: '15px',
            flexDirection: 'row',
            '& a': {
                display: 'flex',
                justifyContent: 'space-between',
                marginRight: 10,
            }
        },

		'& > div': {
			width: '20px',
			display: 'inline-block',
		},

		'&.first-space': {
			borderBottom: '1px solid #eee',
			paddingBottom: '10px',
			marginBottom: '20px',
		},

	},

	navigation__subMenu: {
		overflow: 'hidden'
	},
	navigation__subList: {
		display: 'flex',
		flexDirection: 'column',
		padding: 0,
		margin: 0,
		'.opened > &': {
		}
	},
	navigation__spacing: {
		marginTop: 80,
		borderBottom: `1px solid ${theme.palette.header.backgroundContrast}`,

	},

	burger__trigger: {
		...theme.button.primary,
		display: 'block',
		width: 31,
		height: 31,
		color: theme.palette.primary.color,
		padding: 0,
		'& svg': {
			margin: 'auto',
			width: '100%'
		}
	},
	close__trigger: {
		...theme.button.primary,
		display: 'block',
		width: 25,
		height: 20,
		color: theme.palette.primary.color,
		padding: 0,
		'& svg': {
			margin: 'auto',
			width: '100%'
		}
	}
}

const useStyles = makeStyles(() => styles);

function MobileMenu({menu, footer}) {
	const router = useRouter();
	const classes = useStyles();
	const [isHydrated, setHydrated] = useState(false);
	const { t } = useTranslation(['general', 'account']);
    const isOpen = useApplicationStore((state) => state.modals.menuDrawerOpen);
    const { toggleDrawer } = useApplicationStore((state) => state.methods);

	const {actions: trackingActions} = useContext(TrackingContext);
	const salesChannel = useSessionStore((state) => state.session.salesChannel);

	const {
		translated: {
			customFields: {
				menu_top_links_item_one_text,
				menu_top_links_item_one_url,
				menu_top_links_item_two_text,
				menu_top_links_item_two_url,
				menu_top_links_item_three_text,
				menu_top_links_item_three_url,
				menu_bottom_links_item_one_text,
				menu_bottom_links_item_one_url,
				menu_bottom_links_item_three_text,
				menu_bottom_links_item_three_url,
				menu_bottom_links_item_two_text,
				menu_bottom_links_item_two_url
			}
		}
	} = salesChannel;


	useEffect(() => {
		const handleStart = (url) => {
            toggleDrawer('menuDrawerOpen', false)
		};

		router.events.on('routeChangeStart', handleStart);

		return () => {
			router.events.off('routeChangeStart', handleStart);
		};
	}, [router]);
	
	function renderToplinks() {
        return (
            <>
                {menu_top_links_item_one_text && menu_top_links_item_one_url && (
                    <li className={[classes.navigation__item, classes.toplink].join(' ')}>
                        <Link href={menu_top_links_item_one_url}>
                            <a className={'link'}>{menu_top_links_item_one_text}</a>
                        </Link>
                    </li>
                )}

                {menu_top_links_item_two_text && menu_top_links_item_two_url && (
                    <li className={[classes.navigation__item, classes.toplink].join(' ')}>
                        <Link href={menu_top_links_item_two_url}>
                            <a className={'link'}>{menu_top_links_item_two_text}</a>
                        </Link>
                    </li>
                )}

                {menu_top_links_item_three_text && menu_top_links_item_three_url && (
                    <li className={[classes.navigation__item, classes.toplink].join(' ')}>
                        <Link href={menu_top_links_item_three_url}>
                            <a className={'link'}>{menu_top_links_item_three_text}</a>
                        </Link>
                    </li>
                )}
                <span className={[classes.toplink, 'last-space'].join(' ')}></span>
            </>
        );
    }

    function renderBottomlinks() {
        return (
            <>
                <span className={[classes.bottomlink, 'first-space'].join(' ')}></span>

                <li className={[classes.navigation__item, classes.bottomlink, 'account'].join(' ')}>
                    <Account anchor={'left'} label={t('account:account-link-my-page')} />
                </li>

                {menu_bottom_links_item_one_text && menu_bottom_links_item_one_url && (
                    <li className={[classes.navigation__item, classes.bottomlink].join(' ')}>
                        <Link href={menu_bottom_links_item_one_url}>
                            <a className={'link'}>{menu_bottom_links_item_one_text}</a>
                        </Link>
                    </li>
                )}

                {menu_bottom_links_item_two_text && menu_bottom_links_item_two_url && (
                    <li className={[classes.navigation__item, classes.bottomlink].join(' ')}>
                        <Link href={menu_bottom_links_item_two_url}>
                            <a className={'link'}>{menu_bottom_links_item_two_text}</a>
                        </Link>
                    </li>
                )}

                {menu_bottom_links_item_three_text && menu_bottom_links_item_three_url && (
                    <li className={[classes.navigation__item, classes.bottomlink].join(' ')}>
                        <Link href={menu_bottom_links_item_three_url}>
                            <a className={'link'}>{menu_bottom_links_item_three_text}</a>
                        </Link>
                    </li>
                )}
            </>
        );
    }

    // Renders navigation link tree
    function renderItems(items, level, parent) {
        let renderedItems = [];
        (items || [])
            .filter(item => !item.hideMobile)
            .forEach((item, index) => {
                if (item?.name === 'Nyheter' || item?.name === 'News' || item?.name === 'Neuheiten' || item?.name === 'Nieuw binnen' || item?.name === 'Nyheder' || item?.name === 'Uutuudet') {
                    return renderedItems
                }
                let currentChildren = item.children;

                const visibleChildren = item.children?.filter(child => !child.hideMobile) || [];
                if (visibleChildren.length === 1) {
                    const visibleChildIndex = item.children.findIndex(child => !child.hideMobile);
                    if (visibleChildIndex >= 0) {
                        currentChildren = item.children[visibleChildIndex].children;
                    }
                }
                renderedItems.push(
                    <MobileMenuItem
                        item={item}
                        level={level}
                        classes={{
                            item: classes.navigation__item,
                            subMenu: classes.navigation__subMenu,
                            subList: classes.navigation__subList,
                        }}
                        parent={parent}
                        isMobile={true}
                        key={index}
                    >

                        {item.children.length > 0 && renderItems(currentChildren, level + 1, item)}
                    </MobileMenuItem>,
                );
            });

        return renderedItems;
    }

	// Toggles mobile menu
	function toggleMenu() {

		if (!isOpen && !isHydrated) {
			setHydrated(true);
            toggleDrawer('menuDrawerOpen')
		} else {
            toggleDrawer('menuDrawerOpen')
		}
	}

	// Renders navigation wrapper
	function renderNav() {
		return (
            <nav className={[classes.navigation__wrapper, isOpen ? 'open' : '', 'navigation'].join(' ')}>
                <div className={classes.navigation__inner}>
                    <ul className={classes.navigation__list}>
                        {renderToplinks()}

                        {renderItems(menu, 1, false)}

                        {renderBottomlinks()}
                    </ul>
                </div>
            </nav>
        );
	}

	return (
		<>
			<button className={classes.burger__trigger} onClick={() => {
				toggleMenu();
				trackingActions.click({type: 'menu_open', text: t('menu'), identifier: 'burger_menu'});
			}}>
				{
					isOpen ?
						<CloseIcon className={classes.close__trigger}/>
						:
						<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.5}
								  d="M4 6h16M4 12h16M4 18h16" />
						</svg>
				}
			</button>

			<NewportDrawer anchor="left" open={isOpen} close={toggleMenu} showClose={false}>
				{isOpen ? renderNav() : null}
			</NewportDrawer>
		</>
	)
}

export default memo(MobileMenu);
